import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Container} from 'constants/SampleConstants';
import {ExistingTestOrdersForSampleTestDefinition} from 'sample-tests/ui/test-orders/modals/useExistingTestOrders';
import StorableSearchInput from 'samples/storables/StorableSearchInput';
import {FilterKey, Operator} from 'search/SearchConstants';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import PopoverIcon from 'ui-library/PopoverIcon';
import {ScrollableTableColumn, ScrollableTableRow} from 'ui-library/scrollable/ScrollableTableConstants';

import {
  ProcedureWithTestDefinitions,
  TestDefinitionForModal,
  TestDefinitionSimpleInfo,
  TestOrderCreationRowData,
} from './CreateTestOrdersModal';
import {AllowedDilutionFactor} from './TestOrderCreationTable';

export interface CreateTestOrderInputs {
  procedure: ProcedureWithTestDefinitions;
  selectedSampleId: string;
  fullTestOrderData: Array<TestOrderCreationRowData>;
  setFullTestOrderData: React.Dispatch<React.SetStateAction<Array<TestOrderCreationRowData>>>;
  existingTestOrdersBySampleTestDefinition: ExistingTestOrdersForSampleTestDefinition | undefined;
}

export interface CreateTestOrderOutputs {
  columnHeaders: Array<ScrollableTableColumn>;
  rows: Array<TestOrderRow>;
  handleRowsSelect: (rows: Array<TestOrderRow>, isSelected: boolean) => void;
}

interface TestOrderRowData {
  sampleId: string;
  testDefinitionId: string;
  testDefinition: JSX.Element;
  dilutionFactor: JSX.Element;
  container: JSX.Element;
}
interface TestOrderRow extends ScrollableTableRow {
  data: TestOrderRowData;
}

/** Unique identifier for a row in the table */
const rowIdentifier = (sampleId: string, testDefinitionId: string): string =>
  `${sampleId}-${testDefinitionId}`;

interface ContainerSearchInputProps {
  sampleId: string;
  rowId: string;
  disabled?: boolean;
  handleContainerChange: (id: string, container: Container | null) => void;
  fetchSelectedContainer: (id: string) => Array<Container>;
  autoFocus?: boolean;
}

/**
 * Search input field for finding a container that contains the given sample.
 */
export const ContainerSearchInput: React.FC<ContainerSearchInputProps> = ({
  sampleId,
  rowId,
  disabled,
  handleContainerChange,
  fetchSelectedContainer,
  autoFocus,
}) => (
  <StorableSearchInput
    className={css(styles.containerSearchInput)}
    onChange={(selectedContainers: Array<Container>) => {
      handleContainerChange(rowId, selectedContainers[0] ?? null);
    }}
    storableTypes={[ModelTypeConstants.CONTAINER]}
    shouldAllowMultiple={false}
    disabled={disabled}
    autoFocus={autoFocus}
    placeholder={disabled ? '' : undefined} // Render default placeholder if not disabled, otherwise no text
    selectedStorables={fetchSelectedContainer(rowId)}
    additionalFilters={[
      {
        key: FilterKey.CONTAINER_CONTENTS,
        operator: Operator.HAS_ONE_OF,
        value: {
          contentIds: [sampleId],
        },
      },
    ]}
  />
);

/** Helper function to create a container search input field for a row */
const containerSearchInputForRow = (
  sampleId: string,
  rowId: string,
  disabled: boolean,
  container: Container | null,
  handleContainerChange: (rowId: string, newContainer: Container | null) => void
): JSX.Element => (
  <ContainerSearchInput
    sampleId={sampleId}
    rowId={rowId}
    disabled={disabled}
    handleContainerChange={handleContainerChange}
    fetchSelectedContainer={() => (container != null ? [container] : [])}
  />
);

/** Helper function to create a dilution factor dropdown menu for a row */
const dilutionFactorMenuForRow = (
  rowId: string,
  disabled: boolean,
  dilutionFactorOptions: Array<AllowedDilutionFactor> | undefined,
  dilutionFactor: AllowedDilutionFactor | null,
  handleDilutionFactorChange: (rowId: string, newDilutionFactor: AllowedDilutionFactor | null) => void
): JSX.Element => {
  if (dilutionFactorOptions === undefined) {
    return <></>;
  }
  return (
    <SelectDropdown
      menu={
        <Menu height={Menu.HEIGHTS.SMALL} width={Menu.WIDTHS.SMALL} value={dilutionFactor}>
          {dilutionFactorOptions.map((dilutionFactorOption) => (
            <Option
              key={dilutionFactorOption}
              value={dilutionFactorOption}
              onSelect={(value: AllowedDilutionFactor) => {
                handleDilutionFactorChange(rowId, value);
              }}
            >
              <div>{dilutionFactorOption}</div>
            </Option>
          ))}
        </Menu>
      }
      alwaysDropDown={true}
      displayName={dilutionFactor ?? (disabled ? '' : 'Select dilution factor')}
      shouldEllipsize={true}
      disabled={disabled}
      dataTestAttrs={{component: 'DilutionFactorSelectDropdown', element: 'root'}}
    />
  );
};

const createTestOrderRowData = (
  testDefinitions: Array<TestDefinitionSimpleInfo>,
  selectedSampleId: string,
  procedureId: string,
  prevData: Array<TestOrderCreationRowData>,
  existingTestOrdersBySampleTestDefinition: ExistingTestOrdersForSampleTestDefinition | undefined
): Array<TestOrderCreationRowData> => {
  const newRows: Array<TestOrderCreationRowData> = [];
  // If sample matches, procedure must also match. Remove all rows corresponding to a previous procedure
  // for this sample
  const filteredPrevRows = prevData.filter(
    (row) => row.sample.id !== selectedSampleId || row.assayTemplateId === procedureId
  );

  testDefinitions.forEach((testDefinition) => {
    const rowId = rowIdentifier(selectedSampleId, testDefinition.id);
    if (!filteredPrevRows.some((row) => row.rowId === rowId)) {
      const selectedSampleInfo = existingTestOrdersBySampleTestDefinition?.[selectedSampleId];
      const existingTestOrders = selectedSampleInfo?.testOrdersByTestDefinition?.[testDefinition.id] ?? [];
      newRows.push({
        rowId,
        assayTemplateId: procedureId,
        isSelected: false,
        sample: {
          id: selectedSampleId,
          name: selectedSampleInfo?.name,
        },
        testDefinition: {id: testDefinition.id, name: testDefinition.name},
        container: null,
        dilutionFactor: null,
        hasDilutionFactorError: false,
        existingTestOrdersWarning: null,
        existingTestOrders,
      });
    }
  });

  return [...filteredPrevRows, ...newRows];
};

const extractTestDefintions = (procedure: ProcedureWithTestDefinitions): Array<TestDefinitionSimpleInfo> =>
  procedure.procedureFlowchart?.publishedFlowchartVersion?.nodes.flatMap((node) =>
    node.testDefinitions.map((testDefinition: TestDefinitionForModal) => ({
      id: testDefinition.id,
      name: testDefinition.name,
    }))
  ) ?? [];

const extractDilutionFactors = (
  procedure: ProcedureWithTestDefinitions
): Record<string, Array<AllowedDilutionFactor>> => {
  const dilutions: Record<string, Array<AllowedDilutionFactor>> = {};
  (procedure.procedureFlowchart?.publishedFlowchartVersion?.nodes ?? []).forEach((node) => {
    if (node.dilutionPlan.allowedDilutionFactors.length > 0) {
      node.testDefinitions.forEach((testDefinition: TestDefinitionForModal) => {
        const id = testDefinition.id;
        dilutions[id] = (dilutions[id] || []).concat(node.dilutionPlan.allowedDilutionFactors);
      });
    }
  });
  return dilutions;
};

/**
 * Helper function to create rows for the TestOrderCreationTable.
 *
 * Note that this includes extra information than what gets rendered. This is helpful for building
 * TestOrderInfo objects later, to colocate all the information needed to create a test order.
 */
const createRows = (
  selectedSampleId: string,
  fullTestOrderData: Array<TestOrderCreationRowData>,
  dilutionFactorsByTestDefinition: Record<string, Array<AllowedDilutionFactor>>,
  handleContainerChange: (rowId: string, newContainer: Container | null) => void,
  handleDilutionFactorChange: (rowId: string, newDilutionFactor: AllowedDilutionFactor | null) => void
): Array<TestOrderRow> =>
  fullTestOrderData
    .filter((r) => r.sample.id === selectedSampleId)
    .map((r) => {
      const rowId = r.rowId;
      const isSelected = r.isSelected;
      const isTestDefinitionInaccessible = !r.testDefinition?.id;
      return {
        id: rowId,
        data: {
          sampleId: r.sample.id,
          testDefinitionId: r.testDefinition.id,
          testDefinition: (
            <div>
              <BenchChip id={r.testDefinition.id} size={ChipSize.MINI} />
              {r.existingTestOrdersWarning != null && (
                <PopoverIcon
                  className={css(styles.testDefinitionWarning)}
                  icon="warning"
                  renderContent={() => r.existingTestOrdersWarning}
                />
              )}
            </div>
          ),
          dilutionFactor: dilutionFactorMenuForRow(
            rowId,
            !isSelected,
            dilutionFactorsByTestDefinition[r.testDefinition.id],
            r.dilutionFactor,
            handleDilutionFactorChange
          ),
          container: containerSearchInputForRow(
            r.sample.id,
            rowId,
            !isSelected,
            r.container,
            handleContainerChange
          ),
        },
        isSelected,
        isSelectorDisabled: isTestDefinitionInaccessible,
        // Allow cells to overflow so that warning icons on test definition column don't get cut off by padding
        className: 'scrollableTable--cellOverflow',
      };
    });

const updateRowData = (
  prevData: Array<TestOrderCreationRowData>,
  rowId: string,
  update: (row: TestOrderCreationRowData) => TestOrderCreationRowData
): Array<TestOrderCreationRowData> => prevData.map((row) => (row.rowId === rowId ? update(row) : row));

/** Hook to manage the state of creating  test orders. */
export default function useTestOrderCreationTable({
  procedure,
  selectedSampleId,
  fullTestOrderData,
  setFullTestOrderData,
  existingTestOrdersBySampleTestDefinition,
}: CreateTestOrderInputs): CreateTestOrderOutputs {
  // All test definitions for the procedure
  const testDefinitions: Array<TestDefinitionSimpleInfo> = useMemo(
    () => extractTestDefintions(procedure),
    [procedure]
  );

  const dilutionFactorsByTestDefinition: Record<string, Array<AllowedDilutionFactor>> = useMemo(
    () => extractDilutionFactors(procedure),
    [procedure]
  );

  useEffect(() => {
    setFullTestOrderData((prevData) =>
      createTestOrderRowData(
        testDefinitions,
        selectedSampleId,
        procedure.id,
        prevData,
        existingTestOrdersBySampleTestDefinition
      )
    );
  }, [
    testDefinitions,
    selectedSampleId,
    setFullTestOrderData,
    procedure.id,
    existingTestOrdersBySampleTestDefinition,
  ]);

  const handleContainerChange = useCallback(
    (rowId: string, newContainer: Container | null): void => {
      setFullTestOrderData((prevData) => {
        const updateRow = (row: TestOrderCreationRowData): TestOrderCreationRowData => ({
          ...row,
          container: newContainer,
        });
        return updateRowData(prevData, rowId, updateRow);
      });
    },
    [setFullTestOrderData]
  );

  const handleDilutionFactorChange = useCallback(
    (rowId: string, newDilutionFactor: AllowedDilutionFactor | null): void => {
      setFullTestOrderData((prevData) => {
        const updateRow = (row: TestOrderCreationRowData): TestOrderCreationRowData => ({
          ...row,
          dilutionFactor: newDilutionFactor,
          hasDilutionFactorError:
            newDilutionFactor == null
              ? dilutionFactorsByTestDefinition[row.testDefinition.id]?.length > 0
              : false,
        });
        return updateRowData(prevData, rowId, updateRow);
      });
    },
    [dilutionFactorsByTestDefinition, setFullTestOrderData]
  );

  const handleRowsSelect = useCallback(
    (rows: Array<TestOrderRow>, isSelected: boolean): void => {
      const touchedRowIds = rows.filter((row) => !row.isSelectorDisabled).map((row) => row.id);

      setFullTestOrderData((prevData) =>
        prevData.map((row) =>
          touchedRowIds.includes(row.rowId)
            ? {
                ...row,
                isSelected,
                hasDilutionFactorError:
                  row.dilutionFactor == null
                    ? dilutionFactorsByTestDefinition[row.testDefinition.id]?.length > 0
                    : false,
                existingTestOrdersWarning:
                  isSelected && row.existingTestOrders.length > 0
                    ? `${row.sample.name ?? 'Sample'} already has ${row.testDefinition.name} test order(s)`
                    : null,
              }
            : row
        )
      );
    },
    [dilutionFactorsByTestDefinition, setFullTestOrderData]
  );

  const columnHeaders = [
    {
      header: 'Test definition',
      accessorKey: 'testDefinition',
      flexGrow: 1,
      isCollapsible: false,
      isSortable: false,
    },
    ...(Object.keys(dilutionFactorsByTestDefinition).length > 0
      ? [
          {
            header: 'Dilution factor',
            accessorKey: 'dilutionFactor',
            flexGrow: 2,
            isCollapsible: false,
            isSortable: false,
          },
        ]
      : []), // Only show dilution factor column if dilution plan is available
    {
      header: 'Container',
      accessorKey: 'container',
      flexGrow: 2,
      isCollapsible: false,
      isSortable: false,
    },
  ];

  const rows = useMemo(
    () =>
      createRows(
        selectedSampleId,
        fullTestOrderData,
        dilutionFactorsByTestDefinition,
        handleContainerChange,
        handleDilutionFactorChange
      ),
    [
      selectedSampleId,
      fullTestOrderData,
      dilutionFactorsByTestDefinition,
      handleContainerChange,
      handleDilutionFactorChange,
    ]
  );

  return {
    columnHeaders,
    rows,
    handleRowsSelect,
  };
}

const styles = StyleSheet.create({
  containerSearchInput: {},
  testDefinitionWarning: {
    marginLeft: '6px',
    color: Colors.ORANGE,
  },
});
